import type { RaMasterKind, RaMatrixDefinition } from '@felix/domain';
import { raLinkRatings } from '@felix/domain';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { StatusBadge, type StatusTone } from '../../../StatusBadge.js';
import { LevelBadge } from '../RiskParts.js';
import classes from './RaMaster.module.css';
import type { RaMasterItem, RaMasterView } from './types.js';
import { indexOf } from './view.js';

/**
 * Master Data tablolarının sütunları (K2; ADR 0086 §7), türe göre. Her sütun başlık, hücre ve
 * (sıralanabilirse) sıralama değeri taşır; `drop` dar tabloda gizlenme sırasıdır.
 */

export interface MasterColumn {
  key: string;
  label: string;
  render: (item: RaMasterItem<RaMasterKind>) => ReactNode;
  sort?: (item: RaMasterItem<RaMasterKind>) => string | number | null;
  numeric?: boolean;
  drop?: number;
  wrap?: boolean;
}

const STATUS_TONE: Record<string, StatusTone> = {
  draft: 'warning',
  active: 'success',
  retired: 'neutral',
};

export function MasterStatus({ status, t }: { status: string; t: TFunction }) {
  return (
    <StatusBadge tone={STATUS_TONE[status] ?? 'neutral'}>
      {t(`riskMaster.statuses.${status}`)}
    </StatusBadge>
  );
}

const REVIEW_TONE: Record<string, StatusTone> = {
  draft: 'warning',
  in_review: 'info',
  approved: 'success',
  rejected: 'danger',
  superseded: 'neutral',
};

/** Teknik inceleme rozeti (ADR 0098 §5). */
export function ReviewBadge({ status, t }: { status: string; t: TFunction }) {
  return (
    <StatusBadge tone={REVIEW_TONE[status] ?? 'neutral'}>
      {t(`riskMaster.review.statuses.${status}`)}
    </StatusBadge>
  );
}

const STATUS_ORDER: Record<string, number> = { active: 0, draft: 1, retired: 2 };

export function columnsFor(
  kind: RaMasterKind,
  context: {
    view: RaMasterView;
    t: TFunction;
    matrix: RaMatrixDefinition;
    open: (item: RaMasterItem<RaMasterKind>) => void;
  },
): MasterColumn[] {
  const { view, t, matrix, open } = context;
  const c = (key: string) => t(`riskMaster.columns.${key}`);
  const index = indexOf(view);
  const groups = (list: readonly string[]) =>
    list.map((g) => t(`riskMaster.groups.${g}`)).join(', ');
  const any = (item: RaMasterItem<RaMasterKind>) => item as unknown as Record<string, unknown>;
  const linkCount = (field: 'activityId' | 'hazardId', id: string) =>
    view.links.filter((l) => l[field] === id).length;

  const code: MasterColumn = {
    key: 'code',
    label: c('code'),
    sort: (item) => String(any(item).code),
    render: (item) => (
      <button
        type="button"
        className={classes.codeButton}
        onClick={() => open(item)}
        aria-label={t('riskMaster.actions.open', { code: any(item).code })}
      >
        {String(any(item).code)}
      </button>
    ),
  };
  const status: MasterColumn = {
    key: 'status',
    label: c('status'),
    sort: (item) => STATUS_ORDER[String(any(item).status)] ?? 9,
    render: (item) => <MasterStatus status={String(any(item).status)} t={t} />,
  };
  const name = (sub?: (item: RaMasterItem<RaMasterKind>) => string | null): MasterColumn => ({
    key: 'name',
    label: c('name'),
    wrap: true,
    sort: (item) => String(any(item).name),
    render: (item) => {
      const extra = sub?.(item);
      return (
        <>
          {String(any(item).name)}
          {extra ? <span className={classes.sub}>{extra}</span> : null}
        </>
      );
    },
  });

  switch (kind) {
    case 'categories':
      return [
        code,
        name(),
        {
          key: 'groups',
          label: c('groups'),
          render: (item) => groups((item as RaMasterItem<'categories'>).vesselGroups),
        },
        {
          key: 'rows',
          label: t('riskMaster.tabs.activities'),
          numeric: true,
          drop: 1,
          sort: (item) => view.activities.filter((a) => a.categoryId === item.id).length,
          render: (item) => view.activities.filter((a) => a.categoryId === item.id).length,
        },
        status,
      ];
    case 'activities':
      return [
        code,
        name((item) => (item as RaMasterItem<'activities'>).scope),
        {
          key: 'area',
          label: c('area'),
          drop: 2,
          sort: (item) =>
            index.categories.get((item as RaMasterItem<'activities'>).categoryId)?.name ?? '',
          render: (item) =>
            index.categories.get((item as RaMasterItem<'activities'>).categoryId)?.name ?? '—',
        },
        {
          key: 'groups',
          label: c('groups'),
          drop: 3,
          render: (item) => groups((item as RaMasterItem<'activities'>).vesselGroups),
        },
        {
          key: 'rows',
          label: c('rows'),
          numeric: true,
          drop: 1,
          sort: (item) => linkCount('activityId', item.id),
          render: (item) => linkCount('activityId', item.id),
        },
        // FINAL teknik incelemesi (ADR 0098 §5): onaysız şablon "Draft – not approved"
        {
          key: 'review',
          label: c('review'),
          drop: 4,
          sort: (item) => (item as RaMasterItem<'activities'>).final?.technicalReviewStatus ?? '',
          render: (item) => {
            const review = (item as RaMasterItem<'activities'>).final?.technicalReviewStatus;
            return review ? <ReviewBadge status={review} t={t} /> : '—';
          },
        },
        status,
      ];
    case 'hazards':
      return [
        code,
        name((item) => (item as RaMasterItem<'hazards'>).family),
        {
          key: 'category',
          label: c('category'),
          drop: 3,
          render: (item) => {
            const category = (item as RaMasterItem<'hazards'>).category;
            return category ? t(`riskMaster.hazardCategories.${category}`) : '—';
          },
        },
        {
          key: 'consequences',
          label: c('consequences'),
          wrap: true,
          drop: 2,
          render: (item) =>
            (item as RaMasterItem<'hazards'>).consequenceIds
              .map((id) => index.consequences.get(id)?.name)
              .filter(Boolean)
              .join('; ') || '—',
        },
        {
          key: 'rows',
          label: c('rows'),
          numeric: true,
          drop: 1,
          sort: (item) => linkCount('hazardId', item.id),
          render: (item) => linkCount('hazardId', item.id),
        },
        status,
      ];
    case 'consequences':
      return [
        code,
        name(),
        {
          key: 'rows',
          label: t('riskMaster.tabs.hazards'),
          numeric: true,
          sort: (item) => view.hazards.filter((h) => h.consequenceIds.includes(item.id)).length,
          render: (item) => view.hazards.filter((h) => h.consequenceIds.includes(item.id)).length,
        },
        status,
      ];
    case 'controls':
      return [
        code,
        {
          key: 'measure',
          label: c('measure'),
          wrap: true,
          sort: (item) => (item as RaMasterItem<'controls'>).measure,
          render: (item) => (item as RaMasterItem<'controls'>).measure,
        },
        {
          key: 'hazard',
          label: c('hazard'),
          drop: 2,
          render: (item) => {
            const id = (item as RaMasterItem<'controls'>).hazardId;
            return id ? (index.hazards.get(id)?.code ?? '—') : '—';
          },
        },
        {
          key: 'stage',
          label: c('stage'),
          sort: (item) => (item as RaMasterItem<'controls'>).stage,
          render: (item) => (
            <StatusBadge
              tone={(item as RaMasterItem<'controls'>).stage === 'existing' ? 'info' : 'neutral'}
            >
              {t(`riskMaster.stages.${(item as RaMasterItem<'controls'>).stage}`)}
            </StatusBadge>
          ),
        },
        {
          key: 'hierarchy',
          label: c('hierarchy'),
          drop: 1,
          render: (item) =>
            (item as RaMasterItem<'controls'>).hierarchy
              .map((h) => t(`riskMaster.hierarchy.${h}`))
              .join(' / '),
        },
        status,
      ];
    case 'links':
      return [
        code,
        {
          key: 'activity',
          label: c('activity'),
          wrap: true,
          sort: (item) =>
            index.activities.get((item as RaMasterItem<'links'>).activityId)?.code ?? '',
          render: (item) => {
            const activity = index.activities.get((item as RaMasterItem<'links'>).activityId);
            return (
              <>
                {activity?.name ?? '—'}
                <span className={classes.sub}>{activity?.code}</span>
              </>
            );
          },
        },
        {
          key: 'hazard',
          label: c('hazard'),
          wrap: true,
          sort: (item) => index.hazards.get((item as RaMasterItem<'links'>).hazardId)?.name ?? '',
          render: (item) =>
            index.hazards.get((item as RaMasterItem<'links'>).hazardId)?.name ?? '—',
        },
        {
          key: 'initial',
          label: c('initial'),
          sort: (item) => {
            const l = item as RaMasterItem<'links'>;
            return l.likelihood === null || l.severity === null ? -1 : l.likelihood * l.severity;
          },
          render: (item) => {
            const rating = raLinkRatings(item as RaMasterItem<'links'>, matrix).initial;
            return rating ? (
              <LevelBadge level={rating.level} score={rating.score} />
            ) : (
              <span className={classes.sub}>{t('riskMaster.notAssessed')}</span>
            );
          },
        },
        {
          key: 'residual',
          label: c('residual'),
          drop: 2,
          render: (item) => {
            const rating = raLinkRatings(item as RaMasterItem<'links'>, matrix).residual;
            return rating ? (
              <LevelBadge level={rating.level} score={rating.score} />
            ) : (
              <span className={classes.sub}>{t('riskMaster.notAssessed')}</span>
            );
          },
        },
        {
          key: 'controls',
          label: c('controls'),
          drop: 1,
          render: (item) => {
            const l = item as RaMasterItem<'links'>;
            return t('riskMaster.stats.controlsNote', {
              existing: l.existingControlIds.length,
              additional: l.additionalControlIds.length,
            });
          },
        },
        {
          key: 'controlStatus',
          label: c('controlStatus'),
          drop: 3,
          render: (item) =>
            t(`riskMaster.controlStatuses.${(item as RaMasterItem<'links'>).controlStatus}`),
        },
        status,
      ];
  }
}
